/**
 * msalConfig.js — Microsoft Authentication Library configuration
 *
 * Defines the MSAL configuration for the CalBridge app registration.
 * This single multi-tenant app registration (registered in the SAPPERS
 * Entra ID tenant) handles authentication for BOTH calendar accounts:
 *   - CAL-1: Invero (PST, long-lived token)
 *   - CAL-2: Work 2 (EST, ~12hr token lifetime)
 *
 * The app uses the /common endpoint so users from any M365 tenant
 * can authenticate with their own credentials. No admin consent
 * required — only user-level delegated permissions.
 *
 * SECURITY: This is a public client (SPA) — no client secret.
 * Tokens are stored in browser memory only (never localStorage).
 */

/**
 * Determine redirect URI based on current environment.
 * In local dev (localhost:3000), use the localhost callback.
 * In production, use the custom domain callback.
 * This avoids needing environment variables for a purely client-side app.
 */
const redirectUri = window.location.hostname === 'localhost'
  ? 'http://localhost:3000/auth/callback'
  : 'https://calbridge.sapperstechnologies.com/auth/callback';

export const msalConfig = {
  auth: {
    clientId: 'bab85111-b8e0-4532-9415-5a7af413122c',
    // /common allows any M365 tenant to authenticate
    authority: 'https://login.microsoftonline.com/common',
    redirectUri,
    // Required for multi-account scenarios — without this,
    // MSAL may try to silently acquire tokens for the wrong account.
    navigateToLoginRequestUrl: true,
  },
  cache: {
    // In-memory only — tokens never persisted to disk/storage.
    // This means tokens are lost when the tab is closed,
    // which is the intended security behavior.
    cacheLocation: 'memoryStorage',
    storeAuthStateInCookie: false,
  },
  // Suppress noisy MSAL logs in production
  system: {
    loggerOptions: {
      logLevel: import.meta.env.DEV ? 3 : 0, // Verbose in dev, Error only in prod
    },
  },
};

/**
 * Scopes requested during authentication.
 *
 * Calendars.ReadWrite — read events + write blocker events
 * User.Read — read the authenticated user's profile (for display name)
 *
 * These are delegated (user-consent) permissions. The user approves
 * them on first login; no admin consent is needed.
 */
export const graphScopes = {
  calendar: ['Calendars.ReadWrite', 'User.Read'],
};

/**
 * Calendar source identifiers.
 * Used throughout the app to distinguish which calendar
 * an event belongs to and apply the correct sync rules.
 *
 * Display labels are intentionally generic (A1/A2) — the app
 * should not reveal org names on screen. Under the hood:
 *   CAL1 (A1) = Invero, PST
 *   CAL2 (A2) = Work 2, EST
 *
 * Display cities are also generic:
 *   BC = British Columbia (maps to America/Vancouver, same as LA — Pacific Time)
 *   DC = Washington DC (maps to America/New_York — Eastern Time)
 */
export const calendarSources = {
  CAL1: {
    id: 'CAL-1',
    label: 'A1',                          // Display label (generic)
    displayCity: 'BC',                     // Shown in UI instead of timezone
    timezone: 'America/Vancouver',         // IANA timezone — Pacific Time
    color: '#4A90D9',                      // Blue
  },
  CAL2: {
    id: 'CAL-2',
    label: 'A2',                          // Display label (generic)
    displayCity: 'DC',                     // Shown in UI instead of timezone
    timezone: 'America/New_York',          // IANA timezone — Eastern Time
    color: '#D94A4A',                      // Red
  },
};

/**
 * Sync configuration.
 * Defines the time windows and intervals for the auto-sync engine.
 */
export const syncConfig = {
  // Only sync Invero events within this PST window
  sourceWindowStart: 8,  // 8:00 AM PST
  sourceWindowEnd: 14,   // 2:00 PM PST (14:00)

  // How far back and forward to look for events
  lookbackDays: 7,
  lookaheadDays: 14,

  // Auto-sync interval in milliseconds (2 hours)
  syncIntervalMs: 2 * 60 * 60 * 1000,

  // Stop syncing after this hour (PST)
  syncCutoffHour: 14, // 2:00 PM PST

  // Tag prefix for identifying CalBridge-created blocker events
  blockerTagPrefix: '[CALBRIDGE:',
  blockerTagSuffix: ']',

  // Default blocker event properties
  blockerSubject: 'Busy — External Commitment',
  blockerShowAs: 'oof', // 'oof' or 'busy'
};
